import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  ChevronLeft, ChevronRight, Printer, FileDown, Pencil, Sun, Moon, Bed,
  Copy, RotateCcw, Save, X, CalendarRange, Users, HeartPulse, Umbrella, LogOut, UserPlus,
  LayoutGrid, CalendarDays
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import TurnisticaWeek from "../components/TurnisticaWeek";

const MONTHS = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const DOW = ["Dom","Lun","Mar","Mer","Gio","Ven","Sab"];

const META = {
  DAY:      { label: "GIORNO",   short: "Giorno",   icon: Sun,        chip: "bg-emerald-500/90 text-white ring-emerald-400",  soft: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",  dot: "bg-emerald-400" },
  NIGHT:    { label: "NOTTE",    short: "Notte",    icon: Moon,       chip: "bg-sky-500/90 text-white ring-sky-400",           soft: "bg-sky-500/15 text-sky-300 border-sky-500/30",              dot: "bg-sky-400" },
  REST:     { label: "RIPOSO",   short: "Riposo",   icon: Bed,        chip: "bg-slate-600/90 text-white ring-slate-400",       soft: "bg-slate-500/20 text-slate-300 border-slate-500/30",        dot: "bg-slate-400" },
  SICK:     { label: "MALATTIA", short: "Malattia", icon: HeartPulse, chip: "bg-rose-600/90 text-white ring-rose-400",         soft: "bg-rose-500/15 text-rose-300 border-rose-500/30",           dot: "bg-rose-400" },
  VACATION: { label: "FERIE",    short: "Ferie",    icon: Umbrella,   chip: "bg-indigo-600/90 text-white ring-indigo-400",     soft: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",     dot: "bg-indigo-400" },
  LEAVE:    { label: "PERMESSO", short: "Permesso", icon: LogOut,     chip: "bg-purple-600/90 text-white ring-purple-400",     soft: "bg-purple-500/15 text-purple-300 border-purple-500/30",     dot: "bg-purple-400" },
};
const ABSENCE_TYPES = ["SICK","VACATION","LEAVE"];

function daysInMonth(y, m) { return new Date(y, m, 0).getDate(); }
function iso(y, m, d) { return `${y}-${String(m).padStart(2,"0")}-${String(d).padStart(2,"0")}`; }

export default function Turnistica() {
  const { user } = useAuth();
  const isDriver = user?.role === "autista";
  const [viewMode, setViewMode] = useState("week"); // week | month
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [drivers, setDrivers] = useState([]);
  const [shifts, setShifts] = useState({}); // `${date}|${driver_id}` -> {shift_type, substitute_driver_id, substitute_name, note}
  const [editMode, setEditMode] = useState(false);
  const [staged, setStaged] = useState({}); // pending changes (row or null)
  const [cellMenu, setCellMenu] = useState(null); // {driverId, dateIso}
  const [absenceModal, setAbsenceModal] = useState(null); // {driverId, dateIso, shiftType, driverName, initial}
  const [mobileDriverId, setMobileDriverId] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [drv, sh] = await Promise.all([
      api.get("/drivers"),
      api.get("/shifts", { params: { year, month } }),
    ]);
    const activeDrivers = drv.data.filter(d => d.active !== false);
    const map = {};
    const legacyIds = new Set();
    sh.data.forEach(s => {
      map[`${s.date}|${s.driver_id}`] = {
        shift_type: s.shift_type,
        substitute_driver_id: s.substitute_driver_id || null,
        substitute_name: s.substitute_name || null,
        note: s.note || null,
      };
      if (!activeDrivers.find(d => d.id === s.driver_id)) legacyIds.add(s.driver_id);
    });
    const disabledWithShifts = drv.data.filter(d => d.active === false && legacyIds.has(d.id));
    setDrivers([...activeDrivers, ...disabledWithShifts]);
    setShifts(map);
    setStaged({});
    if (!mobileDriverId && activeDrivers.length) setMobileDriverId(activeDrivers[0].id);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, viewMode]);
  useEffect(() => {
    const close = (e) => { if (!e.target.closest("[data-cell-menu]") && !e.target.closest("[data-shift-cell]")) setCellMenu(null); };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  const numDays = daysInMonth(year, month);
  const rows = useMemo(() => Array.from({ length: numDays }, (_, i) => {
    const day = i + 1;
    const d = iso(year, month, day);
    const dow = new Date(year, month - 1, day).getDay();
    return { day, iso: d, dow, dowLabel: DOW[dow], weekend: dow === 0 || dow === 6 };
  }), [numDays, year, month]);

  // effective shift map = staged overrides shifts
  const effective = useMemo(() => ({ ...shifts, ...staged }), [shifts, staged]);

  const driversById = useMemo(() => {
    const m = {};
    drivers.forEach(d => { m[d.id] = d; });
    return m;
  }, [drivers]);

  const counters = useMemo(() => {
    return drivers.map(d => {
      const c = { DAY:0, NIGHT:0, REST:0, SICK:0, VACATION:0, LEAVE:0 };
      Object.entries(effective).forEach(([k, v]) => {
        const [, did] = k.split("|");
        if (did !== d.id || !v) return;
        const st = v.shift_type;
        if (st in c) c[st]++;
      });
      return { ...d, ...c, TOTAL: c.DAY + c.NIGHT + c.REST + c.SICK + c.VACATION + c.LEAVE };
    });
  }, [drivers, effective]);

  const setShift = async (driverId, dateIso, type) => {
    setCellMenu(null);
    // For absences (SICK/VACATION/LEAVE) open modal to capture substitute + note
    if (ABSENCE_TYPES.includes(type)) {
      const key = `${dateIso}|${driverId}`;
      const drv = drivers.find(x => x.id === driverId);
      const current = effective[key] || {};
      setAbsenceModal({
        driverId, dateIso, shiftType: type,
        driverName: drv ? `${drv.first_name} ${drv.last_name}` : "",
        initial: {
          substitute_driver_id: current.shift_type === type ? (current.substitute_driver_id || "") : "",
          substitute_name: current.shift_type === type ? (current.substitute_name || "") : "",
          note: current.shift_type === type ? (current.note || "") : "",
        }
      });
      return;
    }
    const key = `${dateIso}|${driverId}`;
    const row = { shift_type: type, substitute_driver_id: null, substitute_name: null, note: null };
    if (editMode) {
      setStaged(prev => ({ ...prev, [key]: row }));
      return;
    }
    try {
      await api.post("/shifts", { driver_id: driverId, date: dateIso, shift_type: type });
      setShifts(prev => ({ ...prev, [key]: row }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveAbsence = async (data) => {
    if (!absenceModal) return;
    const { driverId, dateIso, shiftType } = absenceModal;
    const key = `${dateIso}|${driverId}`;
    const row = {
      shift_type: shiftType,
      substitute_driver_id: data.substitute_driver_id || null,
      substitute_name: data.substitute_name || null,
      note: data.note || null,
    };
    if (editMode) {
      setStaged(prev => ({ ...prev, [key]: row }));
      setAbsenceModal(null);
      return;
    }
    try {
      await api.post("/shifts", {
        driver_id: driverId, date: dateIso, shift_type: shiftType,
        substitute_driver_id: row.substitute_driver_id,
        substitute_name: row.substitute_name, note: row.note,
      });
      setShifts(prev => ({ ...prev, [key]: row }));
      setAbsenceModal(null);
      toast.success("Assenza registrata");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const clearShift = async (driverId, dateIso) => {
    setCellMenu(null);
    const key = `${dateIso}|${driverId}`;
    if (editMode) {
      setStaged(prev => ({ ...prev, [key]: null }));
      return;
    }
    try {
      const list = await api.get("/shifts", { params: { year, month, driver_id: driverId } });
      const found = list.data.find(s => s.date === dateIso);
      if (found) await api.delete(`/shifts/${found.id}`);
      setShifts(prev => { const c = { ...prev }; delete c[key]; return c; });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveBatch = async () => {
    setBusy(true);
    try {
      const workItems = [];
      const absenceItems = []; // absences must go through POST /shifts to save sub/note
      const toDelete = [];
      Object.entries(staged).forEach(([k, v]) => {
        const [dateIso, driverId] = k.split("|");
        if (v) {
          if (ABSENCE_TYPES.includes(v.shift_type)) {
            absenceItems.push({
              driver_id: driverId, date: dateIso, shift_type: v.shift_type,
              substitute_driver_id: v.substitute_driver_id, substitute_name: v.substitute_name, note: v.note,
            });
          } else {
            workItems.push({ driver_id: driverId, date: dateIso, shift_type: v.shift_type });
          }
        } else if (shifts[k]) toDelete.push({ driver_id: driverId, date: dateIso });
      });
      if (workItems.length) await api.post("/shifts/bulk", { items: workItems });
      for (const it of absenceItems) { await api.post("/shifts", it); }
      const items = [...workItems, ...absenceItems];
      // For clears, we need to fetch and delete
      if (toDelete.length) {
        const uniqDrivers = [...new Set(toDelete.map(x => x.driver_id))];
        const allShifts = [];
        for (const did of uniqDrivers) {
          const r = await api.get("/shifts", { params: { year, month, driver_id: did } });
          allShifts.push(...r.data);
        }
        for (const del of toDelete) {
          const f = allShifts.find(s => s.date === del.date && s.driver_id === del.driver_id);
          if (f) await api.delete(`/shifts/${f.id}`);
        }
      }
      toast.success(`Salvate ${items.length + toDelete.length} modifiche`);
      setEditMode(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const cancelBatch = () => { setStaged({}); setEditMode(false); };

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y=>y-1); } else setMonth(m=>m-1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(y=>y+1); } else setMonth(m=>m+1); };
  const goToday = () => { const n = new Date(); setYear(n.getFullYear()); setMonth(n.getMonth() + 1); };

  const copyPrevious = async () => {
    if (!confirm(`Copiare i turni di ${MONTHS[(month + 10) % 12]} nel mese di ${MONTHS[month-1]} ${year}?`)) return;
    try {
      const r = await api.post("/shifts/copy-previous-month", { year, month });
      toast.success(`Copiati ${r.data.created} turni dal mese precedente`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const resetMonth = async () => {
    if (!confirm(`ATTENZIONE: eliminare TUTTI i turni di ${MONTHS[month-1]} ${year}? Operazione irreversibile.`)) return;
    try {
      const r = await api.delete("/shifts/month", { params: { year, month } });
      toast.success(`Eliminati ${r.data.deleted} turni`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get("/shifts/pdf", { params: { year, month }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `turnistica_${year}-${String(month).padStart(2,"0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const exportCSV = () => {
    const header = ["Data","Giorno", ...drivers.map(d => `${d.first_name} ${d.last_name}`)];
    const lines = [header.join(";")];
    rows.forEach(r => {
      const line = [`${String(r.day).padStart(2,"0")}/${String(month).padStart(2,"0")}/${year}`, r.dowLabel];
      drivers.forEach(d => { line.push(effective[`${r.iso}|${d.id}`] || ""); });
      lines.push(line.join(";"));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `turnistica_${year}-${String(month).padStart(2,"0")}.csv`; a.click();
  };
  const doImport = async () => {
    try {
      const lines = importText.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) { toast.error("Formato non valido"); return; }
      const header = lines[0].split(/[;,\t]/).map(s => s.trim());
      const nameCols = header.slice(2).map(h => h.toUpperCase());
      const items = [];
      lines.slice(1).forEach(l => {
        const cols = l.split(/[;,\t]/).map(s => s.trim());
        const dateRaw = cols[0]; if (!dateRaw) return;
        // support dd/mm/yyyy or yyyy-mm-dd
        let dateIso = dateRaw;
        const m1 = dateRaw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (m1) dateIso = `${m1[3]}-${m1[2].padStart(2,"0")}-${m1[1].padStart(2,"0")}`;
        nameCols.forEach((n, i) => {
          const cell = (cols[2 + i] || "").toUpperCase().trim();
          let st = null;
          if (["G","DAY","GIORNO"].includes(cell)) st = "DAY";
          else if (["N","NIGHT","NOTTE"].includes(cell)) st = "NIGHT";
          else if (["R","REST","RIPOSO"].includes(cell)) st = "REST";
          if (!st) return;
          const drv = drivers.find(d => `${d.first_name} ${d.last_name}`.toUpperCase() === n);
          if (drv) items.push({ driver_id: drv.id, date: dateIso, shift_type: st });
        });
      });
      if (!items.length) { toast.error("Nessuna riga valida rilevata"); return; }
      const r = await api.post("/shifts/bulk", { items });
      toast.success(`Importate ${r.data.saved} righe`);
      setImportOpen(false); setImportText("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const monthLabel = `${MONTHS[month-1]} ${year}`;

  // Week view mode (default) uses standalone component
  if (viewMode === "week" && !isDriver) {
    return (
      <div className="space-y-3 pb-16">
        <div className="flex justify-end">
          <div className="inline-flex rounded-xl border border-slate-800 bg-slate-900/50 p-1">
            <button data-testid="view-week" onClick={()=>setViewMode("week")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-500 text-slate-950"><CalendarDays size={14}/> Settimana</button>
            <button data-testid="view-month" onClick={()=>setViewMode("month")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-400 hover:text-slate-200"><LayoutGrid size={14}/> Mese</button>
          </div>
        </div>
        <TurnisticaWeek/>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-16" data-testid="turnistica-page">
      {!isDriver && (
        <div className="flex justify-end -mb-3">
          <div className="inline-flex rounded-xl border border-slate-800 bg-slate-900/50 p-1">
            <button data-testid="view-week" onClick={()=>setViewMode("week")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-400 hover:text-slate-200"><CalendarDays size={14}/> Settimana</button>
            <button data-testid="view-month" onClick={()=>setViewMode("month")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-500 text-slate-950"><LayoutGrid size={14}/> Mese</button>
          </div>
        </div>
      )}      {/* HEADER */}
      <div className="rounded-2xl border border-slate-800/80 bg-gradient-to-br from-slate-900 to-slate-950 p-5 no-print">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/15 border border-amber-500/30"><CalendarRange className="text-amber-400" size={20}/></div>
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white tracking-tight">Turnistica</h1>
            </div>
            <div className="text-sm text-slate-400 mt-1.5 ml-12">Organizza i turni dei tuoi autisti</div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 rounded-xl border border-slate-800 bg-slate-950/60 p-1">
              <button data-testid="prev-month-btn" onClick={prevMonth} className="p-2 rounded-lg hover:bg-slate-800 text-slate-300"><ChevronLeft size={18}/></button>
              <div className="min-w-[170px] text-center font-display font-extrabold uppercase tracking-wide text-white px-2">{monthLabel}</div>
              <button data-testid="next-month-btn" onClick={nextMonth} className="p-2 rounded-lg hover:bg-slate-800 text-slate-300"><ChevronRight size={18}/></button>
            </div>
            <Button variant="outline" data-testid="today-btn" className="border-slate-800 bg-transparent text-slate-300 h-10" onClick={goToday}>Oggi</Button>
            <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300 h-10" onClick={downloadPdf} data-testid="pdf-shifts-btn"><Printer size={16}/> Stampa PDF</Button>
            <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300 h-10" onClick={exportCSV} data-testid="export-csv-btn"><FileDown size={16}/> Esporta</Button>
            {!isDriver && (
              editMode ? (
                <>
                  <Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold h-10" onClick={saveBatch} disabled={busy} data-testid="save-batch-btn"><Save size={16}/> Salva modifiche</Button>
                  <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300 h-10" onClick={cancelBatch}><X size={16}/> Annulla</Button>
                </>
              ) : (
                <Button className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold h-10" onClick={() => setEditMode(true)} data-testid="edit-mode-btn"><Pencil size={16}/> Modifica Turni</Button>
              )
            )}
          </div>
        </div>
      </div>

      {/* DRIVER SUMMARY CARDS */}
      {counters.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {counters.map(d => (
            <div key={d.id} className="rounded-xl border border-slate-800/80 bg-slate-900/60 p-4 hover:border-amber-500/30 transition-colors" data-testid={`driver-card-${d.id}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-amber-400 font-bold text-sm">{(d.first_name||"?")[0]}{(d.last_name||"?")[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold text-sm truncate">{d.first_name} {d.last_name}</div>
                  {d.active === false && <div className="text-[10px] text-slate-500 italic">disattivato</div>}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <Counter icon={Sun} label="Giorno" value={d.DAY} tone={META.DAY}/>
                <Counter icon={Moon} label="Notte" value={d.NIGHT} tone={META.NIGHT}/>
                <Counter icon={Bed} label="Riposo" value={d.REST} tone={META.REST}/>
                <Counter icon={HeartPulse} label="Malat." value={d.SICK} tone={META.SICK}/>
                <Counter icon={Umbrella} label="Ferie" value={d.VACATION} tone={META.VACATION}/>
                <Counter icon={LogOut} label="Perm." value={d.LEAVE} tone={META.LEAVE}/>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CALENDAR CARD */}
      <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-5 border-b border-slate-800/60">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Calendario</div>
            <div className="font-display text-2xl font-extrabold text-white uppercase tracking-wide">{monthLabel}</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <LegendChip meta={META.DAY}/>
            <LegendChip meta={META.NIGHT}/>
            <LegendChip meta={META.REST}/>
            <LegendChip meta={META.SICK}/>
            <LegendChip meta={META.VACATION}/>
            <LegendChip meta={META.LEAVE}/>
          </div>
        </div>

        {drivers.length === 0 ? (
          <div className="p-10 text-center text-slate-500">Nessun autista attivo. Aggiungine uno dalla sezione <b className="text-slate-300">Autisti</b>.</div>
        ) : (
          <>
            {/* Desktop / Tablet TABLE */}
            <div className="hidden md:block overflow-x-auto" data-testid="shift-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-950/60">
                  <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
                    <th className="p-3 text-left sticky left-0 bg-slate-950/95 z-10">Data</th>
                    <th className="p-3 text-left">Giorno</th>
                    {drivers.map(d => (
                      <th key={d.id} className={`p-3 text-center min-w-[130px] ${d.active === false ? "text-slate-600 italic" : "text-slate-300"}`}>
                        {d.first_name} {d.last_name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.day} className={r.weekend ? "bg-amber-500/[0.04]" : "hover:bg-slate-900/40"}>
                      <td className="p-2.5 sticky left-0 bg-slate-950/90 z-10 font-mono-num text-slate-100 font-bold border-r border-slate-800/60">{String(r.day).padStart(2,"0")}/{String(month).padStart(2,"0")}</td>
                      <td className={`p-2.5 font-mono-num text-xs ${r.weekend ? "text-amber-400 font-bold" : "text-slate-400"}`}>{r.dowLabel}</td>
                      {drivers.map(d => {
                        const key = `${r.iso}|${d.id}`;
                        const row = effective[key];
                        const dirty = key in staged;
                        return (
                          <td key={d.id} className="p-1.5 text-center relative">
                            <ShiftPill row={row} dirty={dirty}
                              driversById={driversById}
                              canEdit={!isDriver && d.active !== false}
                              onClick={(e) => {
                                if (isDriver || d.active === false) return;
                                const rect = e.currentTarget.getBoundingClientRect();
                                setCellMenu({ driverId: d.id, dateIso: r.iso, x: rect.left, y: rect.bottom });
                              }}
                              testid={`cell-${r.iso}-${d.id}`}/>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: driver selector + vertical list */}
            <div className="md:hidden p-4 space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num flex items-center gap-1"><Users size={11}/> Seleziona autista</label>
                <Select value={mobileDriverId} onValueChange={setMobileDriverId}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 mt-1" data-testid="mobile-driver-select"><SelectValue/></SelectTrigger>
                  <SelectContent className="bg-slate-950 border-slate-800">
                    {drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                {rows.map(r => {
                  const key = `${r.iso}|${mobileDriverId}`;
                  const row = effective[key];
                  const drv = drivers.find(x => x.id === mobileDriverId);
                  const canEdit = !isDriver && drv?.active !== false;
                  return (
                    <div key={r.day} className={`flex items-center justify-between rounded-lg border ${r.weekend ? "border-amber-500/20 bg-amber-500/5" : "border-slate-800 bg-slate-950/50"} p-3`}>
                      <div>
                        <div className="text-slate-200 font-bold font-mono-num">{String(r.day).padStart(2,"0")}/{String(month).padStart(2,"0")}</div>
                        <div className={`text-xs ${r.weekend ? "text-amber-400" : "text-slate-500"}`}>{r.dowLabel}</div>
                      </div>
                      <ShiftPill row={row} driversById={driversById} canEdit={canEdit}
                        onClick={(e) => {
                          if (!canEdit) return;
                          const rect = e.currentTarget.getBoundingClientRect();
                          setCellMenu({ driverId: mobileDriverId, dateIso: r.iso, x: Math.max(16, rect.left - 100), y: rect.bottom });
                        }}
                        testid={`mob-cell-${r.iso}`}/>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {/* SUMMARY TABLE + QUICK ACTIONS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden">
          <div className="p-5 border-b border-slate-800/60">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Report</div>
            <div className="font-display text-xl font-bold text-white">Riepilogo mensile</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
                <tr>
                  <th className="p-3 text-left">Autista</th>
                  <th className="text-right">G</th>
                  <th className="text-right">N</th>
                  <th className="text-right">R</th>
                  <th className="text-right">Mal.</th>
                  <th className="text-right">Fer.</th>
                  <th className="text-right">Perm.</th>
                  <th className="text-right pr-4">Tot.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {counters.map(d => (
                  <tr key={d.id} className="hover:bg-slate-900/40">
                    <td className="p-3 text-white font-bold">{d.first_name} {d.last_name}</td>
                    <td className="text-right font-mono-num text-emerald-400 font-bold">{d.DAY}</td>
                    <td className="text-right font-mono-num text-sky-400 font-bold">{d.NIGHT}</td>
                    <td className="text-right font-mono-num text-slate-300 font-bold">{d.REST}</td>
                    <td className="text-right font-mono-num text-rose-400 font-bold">{d.SICK}</td>
                    <td className="text-right font-mono-num text-indigo-400 font-bold">{d.VACATION}</td>
                    <td className="text-right font-mono-num text-purple-400 font-bold">{d.LEAVE}</td>
                    <td className="text-right pr-4 font-mono-num text-amber-400 font-extrabold text-base">{d.TOTAL}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {!isDriver && (
          <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 p-5 space-y-2.5">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Azioni rapide</div>
            <div className="font-display text-xl font-bold text-white mb-2">Strumenti</div>
            <QuickAction icon={Copy} label="Copia mese precedente" hint="Duplica lo schema del mese scorso" onClick={copyPrevious} testid="copy-prev-btn"/>
            <QuickAction icon={RotateCcw} label="Reset turni" hint="Elimina tutti i turni del mese" tone="rose" onClick={resetMonth} testid="reset-month-btn"/>
            <QuickAction icon={FileDown} label="Importa da CSV" hint="Formato: Data;Giorno;Autista1;..." onClick={() => setImportOpen(true)} testid="import-btn"/>
            <QuickAction icon={Printer} label="Esporta PDF" hint="Genera PDF orizzontale A4" onClick={downloadPdf}/>
          </div>
        )}
      </div>

      {/* CELL MENU POPOVER */}
      {cellMenu && (
        <div
          data-cell-menu
          className="fixed z-50 rounded-xl border border-slate-700 bg-slate-950 shadow-2xl p-1.5 min-w-[180px]"
          style={{ left: Math.min(cellMenu.x, window.innerWidth - 200), top: cellMenu.y + 4 }}
        >
          <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Turno di lavoro</div>
          {(["DAY","NIGHT","REST"]).map(t => {
            const M = META[t]; const Icon = M.icon;
            return (
              <button key={t} data-testid={`menu-${t}`} onClick={() => setShift(cellMenu.driverId, cellMenu.dateIso, t)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg hover:bg-slate-800 text-slate-200">
                <span className={`w-3 h-3 rounded-full ${M.dot}`}/>
                <Icon size={14} className="text-slate-400"/>
                <span className="font-bold">{M.label}</span>
              </button>
            );
          })}
          <div className="border-t border-slate-800 my-1"/>
          <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Assenza</div>
          {(["SICK","VACATION","LEAVE"]).map(t => {
            const M = META[t]; const Icon = M.icon;
            return (
              <button key={t} data-testid={`menu-${t}`} onClick={() => setShift(cellMenu.driverId, cellMenu.dateIso, t)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg hover:bg-slate-800 text-slate-200">
                <span className={`w-3 h-3 rounded-full ${M.dot}`}/>
                <Icon size={14} className="text-slate-400"/>
                <span className="font-bold">{M.label}</span>
                <UserPlus size={11} className="text-slate-500 ml-auto" title="Richiede sostituto"/>
              </button>
            );
          })}
          <div className="border-t border-slate-800 my-1"/>
          <button onClick={() => clearShift(cellMenu.driverId, cellMenu.dateIso)}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg hover:bg-rose-500/20 text-rose-400" data-testid="menu-clear">
            <X size={14}/> Rimuovi turno
          </button>
        </div>
      )}

      {/* ABSENCE MODAL */}
      {absenceModal && (
        <AbsenceDialog
          data={absenceModal}
          drivers={drivers.filter(d => d.id !== absenceModal.driverId && d.active !== false)}
          onClose={() => setAbsenceModal(null)}
          onSave={saveAbsence}
        />
      )}

      {/* IMPORT DIALOG */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="bg-slate-950 border-slate-800 max-w-2xl text-slate-200">
          <DialogHeader><DialogTitle className="font-display text-2xl text-white">Importa Turnistica CSV</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-400 mb-2">
            Formato: <span className="font-mono-num text-slate-300">Data;Giorno;NOME COGNOME 1;NOME COGNOME 2;...</span><br/>
            Valori accettati: <b className="text-emerald-400">G/DAY/GIORNO</b>, <b className="text-sky-400">N/NIGHT/NOTTE</b>, <b className="text-slate-400">R/REST/RIPOSO</b>. Data in <b>dd/mm/yyyy</b> o <b>yyyy-mm-dd</b>.
          </div>
          <textarea value={importText} onChange={e => setImportText(e.target.value)} rows={10} placeholder={"Data;Giorno;MARIO ROSSI;LUCA BIANCHI\n01/09/2026;Mar;G;N\n02/09/2026;Mer;N;R"} className="w-full bg-slate-900 border border-slate-800 rounded-lg p-3 font-mono-num text-xs" data-testid="import-textarea"/>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300" onClick={() => setImportOpen(false)}>Annulla</Button>
            <Button className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold" onClick={doImport} data-testid="confirm-import-btn"><Save size={16}/> Importa</Button>
          </div>
        </DialogContent>
      </Dialog>

      {editMode && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 rounded-full border border-amber-500/40 bg-amber-500/15 text-amber-200 px-4 py-2 text-sm font-bold shadow-2xl backdrop-blur no-print z-40" data-testid="edit-mode-banner">
          🔧 Modalità Modifica: {Object.keys(staged).length} modifiche in sospeso — premi Salva per confermare
        </div>
      )}
    </div>
  );
}

function ShiftPill({ row, dirty, canEdit, onClick, testid, driversById }) {
  const st = row?.shift_type;
  const M = st ? META[st] : null;
  const Icon = M?.icon;
  if (!M) {
    return (
      <button type="button" data-shift-cell data-testid={testid} disabled={!canEdit} onClick={onClick}
        className={`w-full h-10 rounded-lg border border-dashed transition-all ${
          canEdit ? "border-slate-800 text-slate-600 hover:border-amber-500 hover:text-amber-400 cursor-pointer" : "border-slate-800/50 text-slate-700 cursor-default"
        } ${dirty ? "ring-2 ring-amber-500" : ""}`}>
        <span className="text-xs">+</span>
      </button>
    );
  }
  const isAbsence = ABSENCE_TYPES.includes(st);
  let subLabel = null;
  if (isAbsence) {
    if (row.substitute_driver_id && driversById && driversById[row.substitute_driver_id]) {
      const d = driversById[row.substitute_driver_id];
      subLabel = `${d.first_name} ${d.last_name}`;
    } else if (row.substitute_name) {
      subLabel = row.substitute_name;
    }
  }
  return (
    <div className="flex flex-col items-center gap-0.5">
      <button type="button" data-shift-cell data-testid={testid} disabled={!canEdit} onClick={onClick}
        className={`w-full h-10 rounded-lg flex items-center justify-center gap-1.5 font-bold text-xs uppercase tracking-widest shadow ring-1 ring-inset transition-all ${M.chip} ${
          canEdit ? "hover:brightness-110 cursor-pointer" : "cursor-default"
        } ${dirty ? "outline outline-2 outline-amber-500" : ""}`}>
        <Icon size={13}/><span>{M.short}</span>
      </button>
      {subLabel && (
        <div className="text-[9px] leading-tight text-slate-400 truncate max-w-full font-mono-num" title={`Sostituto: ${subLabel}${row.note ? ' · ' + row.note : ''}`}>
          → {subLabel}
        </div>
      )}
    </div>
  );
}

function Counter({ icon: Icon, label, value, tone }) {
  return (
    <div className={`rounded-lg border p-1.5 ${tone.soft}`}>
      <div className="flex items-center justify-center gap-1 text-[9px] uppercase tracking-widest opacity-80 font-mono-num"><Icon size={9}/>{label}</div>
      <div className="font-mono-num font-extrabold text-base leading-tight">{value}</div>
    </div>
  );
}

function AbsenceDialog({ data, drivers, onClose, onSave }) {
  const M = META[data.shiftType];
  const Icon = M.icon;
  const [subId, setSubId] = useState(data.initial?.substitute_driver_id || "");
  const [subFree, setSubFree] = useState(data.initial?.substitute_name || "");
  const [note, setNote] = useState(data.initial?.note || "");
  const [subMode, setSubMode] = useState(data.initial?.substitute_name && !data.initial?.substitute_driver_id ? "free" : "list");
  const submit = () => {
    onSave({
      substitute_driver_id: subMode === "list" ? (subId || null) : null,
      substitute_name: subMode === "free" ? (subFree.trim() || null) : null,
      note: note.trim() || null,
    });
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="absence-modal" className="bg-slate-950 border-slate-800 max-w-md text-slate-200">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-white flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-widest ring-1 ring-inset ${M.chip}`}>
              <Icon size={13}/> {M.label}
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Autista assente</div>
            <div className="text-white font-bold">{data.driverName}</div>
            <div className="text-xs text-slate-400 font-mono-num">{data.dateIso}</div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Sostituito da (opzionale)</div>
            <div className="flex gap-1 mb-2 rounded-lg border border-slate-800 bg-slate-900/60 p-1 text-xs">
              <button type="button" onClick={() => setSubMode("list")} data-testid="sub-mode-list"
                className={`flex-1 px-2 py-1.5 rounded ${subMode === "list" ? "bg-amber-500 text-slate-950 font-bold" : "text-slate-400 hover:text-slate-200"}`}>
                Autista sistema
              </button>
              <button type="button" onClick={() => setSubMode("free")} data-testid="sub-mode-free"
                className={`flex-1 px-2 py-1.5 rounded ${subMode === "free" ? "bg-amber-500 text-slate-950 font-bold" : "text-slate-400 hover:text-slate-200"}`}>
                Nome libero
              </button>
            </div>
            {subMode === "list" ? (
              <Select value={subId || "none"} onValueChange={(v) => setSubId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="substitute-select" className="bg-slate-900 border-slate-800"><SelectValue placeholder="Seleziona sostituto"/></SelectTrigger>
                <SelectContent className="bg-slate-950 border-slate-800">
                  <SelectItem value="none">-- Nessuno --</SelectItem>
                  {drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input data-testid="substitute-free-input" value={subFree} onChange={(e) => setSubFree(e.target.value)}
                placeholder="Es. Autista esterno Mario B." className="bg-slate-900 border-slate-800"/>
            )}
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Nota / Motivo (opzionale)</div>
            <Textarea data-testid="absence-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Es. Certificato medico n.123 - fino al 28/02" className="bg-slate-900 border-slate-800"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300" onClick={onClose} data-testid="absence-cancel">Annulla</Button>
          <Button className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold" onClick={submit} data-testid="absence-save"><Save size={14}/> Salva</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
function LegendChip({ meta }) {
  const Icon = meta.icon;
  return (
    <div className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950/60 px-2.5 py-1">
      <span className={`w-2.5 h-2.5 rounded-full ${meta.dot}`}/>
      <Icon size={12} className="text-slate-400"/>
      <span className="text-[10px] uppercase tracking-widest text-slate-300 font-mono-num font-bold">{meta.label}</span>
    </div>
  );
}
function QuickAction({ icon: Icon, label, hint, onClick, tone, testid }) {
  const t = tone === "rose" ? "hover:border-rose-500/50 hover:text-rose-300" : "hover:border-amber-500/50 hover:text-amber-300";
  return (
    <button onClick={onClick} data-testid={testid}
      className={`w-full flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-950/40 p-3 text-left transition-all ${t} group`}>
      <div className="p-2 rounded-lg bg-slate-900 group-hover:bg-slate-800 text-slate-400"><Icon size={16}/></div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-slate-100">{label}</div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num truncate">{hint}</div>
      </div>
    </button>
  );
}
